import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  PayTransactionRequest,
  PayTransactionResponse,
} from "Infrastructure/Api/Api";
import { postTransactionsPay } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { trackPayRedirectToBank } from "Infrastructure/Utils/MixpanelUtils";
import { Resources, useResource } from "Translations/Resources";
import { transactionsRefundsKeys } from "Api/Queries/Transactions/useTransactionsRefunds";
import { isIOS } from "Utils/Platform/PlatformUtils";
import { appUrl } from "Utils/UrlUtils";
import { useRef } from "react";
import { toast } from "sonner";

const RESOURCES = Resources.TransactionErrors;

export const useTransactionPay = () => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  // When the pay request left the browser — measured against the moment we leave for the bank.
  const payStartedAt = useRef<number | null>(null);

  return useMutation({
    onMutate: () => {
      payStartedAt.current = performance.now();
    },
    mutationFn: processResponseWithApiProblemDetails(
      // shouldReturnToNativeApp is decided here (via isIOS()), not by callers, so it is omitted
      // from the input type.
      (request: Omit<PayTransactionRequest, "shouldReturnToNativeApp">) =>
        postTransactionsPay({
          ...request,
          // Only the iOS Capacitor shell handles the moone:// return scheme. getPlatform() is
          // "ios" only inside that shell (not in mobile Safari), so isIOS() is the precise check.
          shouldReturnToNativeApp: isIOS(),
        }),
    ),
    onSuccess: (
      data: PayTransactionResponse,
      variables: Omit<PayTransactionRequest, "shouldReturnToNativeApp">,
    ) => {
      queryClient.invalidateQueries({
        queryKey: transactionsRefundsKeys.all,
      });
      if (data.signingUrl) {
        trackPayRedirectToBank(data.signingUrl, payStartedAt.current);
        window.location.href = data.signingUrl;
      } else if (data.isServerAuthorized) {
        // Payment was authorized server-side (e.g., CZKT) — no bank redirect needed.
        window.location.href = `${appUrl("transaction-detail")}/${variables.publicID}`;
      } else {
        toast.error(t(RESOURCES.UnknownPaymentError));
      }
    },
    onError: undefined, // this disables the global handler from App.tsx (so we don't show toast 2x since we have custom error handling)
  });
};
